import { LOADING_CHILD_LANES } from '../TimelineItemConstants';
import type { TimelineItemType } from '../useTimelineTableColumns';
import { getNormalizePlannedDates } from './timelineDateUtils';

export type ItemWithStackingOrder = TimelineItemType & { order?: number; progress?: number };

const DAY_MS = 24 * 60 * 60 * 1000;

const itemStartMs = (item: TimelineItemType, fallbackMs: number): number => {
  const { start } = getNormalizePlannedDates(item.plannedStartDateTime, item.plannedFinishDateTime);
  return start ? new Date(start).getTime() : fallbackMs;
};

// Date span of an item plus any children that spill past its own start/finish.
// An undated parent derives its span from its children rather than spanning all time.
export const getExpandedFootprintInterval = (
  item: TimelineItemType,
): { startMs: number; endMs: number } => {
  const dates = getNormalizePlannedDates(item.plannedStartDateTime, item.plannedFinishDateTime);
  const starts: number[] = [];
  const ends: number[] = [];
  if (dates.start) starts.push(new Date(dates.start).getTime());
  if (dates.finish) ends.push(new Date(dates.finish).getTime());

  for (const child of item.children ?? []) {
    const childDates = getNormalizePlannedDates(
      child.plannedStartDateTime,
      child.plannedFinishDateTime,
    );
    if (!childDates.hasAnyDate) continue;
    if (childDates.start) starts.push(new Date(childDates.start).getTime());
    if (childDates.finish) ends.push(new Date(childDates.finish).getTime());
  }

  return {
    startMs: starts.length ? Math.min(...starts) : -Infinity,
    endMs: ends.length ? Math.max(...ends) : Infinity,
  };
};

// Pick the item nearest to a lane position, breaking ties by date distance.
const nearestByLaneThenDate = (
  candidates: ItemWithStackingOrder[],
  dropLane: number,
  dropStartMs: number,
  dayWidth: number,
): ItemWithStackingOrder | undefined => {
  let nearest: ItemWithStackingOrder | undefined;
  let nearestLaneDist = Number.POSITIVE_INFINITY;
  let nearestDateDist = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) {
    const laneDist = Math.abs((candidate.order ?? 0) + 0.5 - dropLane);
    const dateDist = Math.abs(
      ((itemStartMs(candidate, dropStartMs) - dropStartMs) / DAY_MS) * dayWidth,
    );
    if (
      laneDist < nearestLaneDist - 1e-6 ||
      (Math.abs(laneDist - nearestLaneDist) <= 1e-6 && dateDist < nearestDateDist)
    ) {
      nearestLaneDist = laneDist;
      nearestDateDist = dateDist;
      nearest = candidate;
    }
  }
  return nearest;
};

/**
 * Translates a 2D drop point (lane + date) into the rank neighbours for the
 * dropped item. Stacking places non-clashing items on the same order (same lane,
 * different dates), so we can't infer intent from a lane index alone: we find the
 * nearest item in pixel space, then place above or below it based on where the
 * drop landed relative to that item.
 *
 * When the drop sits between two stacked lanes we return BOTH neighbours so the
 * caller can bracket the rank with `between()`; a one-sided rank would `genNext`
 * past any third item on the far side of the chosen neighbour.
 */
export const resolveDropNeighbors = (
  otherItems: ItemWithStackingOrder[],
  dropLane: number,
  dropStartMs: number,
  _laneHeight: number,
  dayWidth: number,
): { previousItemId?: number; nextItemId?: number } => {
  // Vertical position decides which gap the drop is in; the date only breaks
  // ties between items sharing a lane. Picking by raw 2D distance would let a
  // date-aligned item on another lane win and snap the drop to the wrong gap.
  const nearest = nearestByLaneThenDate(otherItems, dropLane, dropStartMs, dayWidth);
  if (!nearest) return {};

  const nearestOrder = nearest.order ?? 0;
  const nearestCenter = nearestOrder + 0.5;
  // The date tie-break only means something when `nearest` shares its lane with
  // another item (side-by-side): then a level drop picks left/right by date. When
  // `nearest` is the sole occupant of its lane there is no left/right to choose,
  // so a centre-band drop is just "this lane's gap" and must be decided by lane
  // position — otherwise the date can shove the item past a stacked neighbour.
  const hasSameLaneSibling = otherItems.some(
    (item) => item.id !== nearest.id && (item.order ?? 0) === nearestOrder,
  );
  // Clearly above the item's center -> before it; clearly below -> after it.
  const EDGE = 0.2;
  let dropsAfterNearest: boolean;
  let decidedByLane: boolean;
  if (hasSameLaneSibling && dropLane >= nearestCenter - EDGE && dropLane <= nearestCenter + EDGE) {
    dropsAfterNearest = dropStartMs >= itemStartMs(nearest, dropStartMs);
    decidedByLane = false;
  } else {
    dropsAfterNearest = dropLane > nearestCenter;
    decidedByLane = true;
  }

  // Bracket only when the gap was chosen by lane position: the opposite neighbour
  // is the closest item on the far side of `nearest`. We take the nearest lane
  // strictly beyond `nearest` in the drop direction rather than `nearestOrder ± 1`
  // — the dragged item is filtered out of `otherItems`, leaving a gap in the order
  // sequence, so the immediately-adjacent order may be empty. A same-lane (date)
  // decision keeps its single neighbour: side-by-side items share an order, so
  // there is no vertical neighbour to bracket against.
  if (decidedByLane) {
    const farSide = otherItems.filter((item) =>
      dropsAfterNearest ? (item.order ?? 0) > nearestOrder : (item.order ?? 0) < nearestOrder,
    );
    const oppositeOrder = farSide.reduce<number | undefined>((best, item) => {
      const order = item.order ?? 0;
      if (best === undefined) return order;
      // Closest lane to `nearest`: smallest order above, or largest order below.
      return dropsAfterNearest ? Math.min(best, order) : Math.max(best, order);
    }, undefined);
    const opposite =
      oppositeOrder === undefined
        ? undefined
        : nearestByLaneThenDate(
            farSide.filter((item) => (item.order ?? 0) === oppositeOrder),
            dropLane,
            dropStartMs,
            dayWidth,
          );
    if (opposite) {
      return dropsAfterNearest
        ? { previousItemId: nearest.id, nextItemId: opposite.id }
        : { previousItemId: opposite.id, nextItemId: nearest.id };
    }
  }

  return dropsAfterNearest ? { previousItemId: nearest.id } : { nextItemId: nearest.id };
};

export const calculateChildLanes = (
  item: TimelineItemType,
  cache?: Map<number, number>,
): number => {
  if (cache) {
    const cached = cache.get(item.id);
    if (cached !== undefined) return cached;
  }
  if (!item.children || item.children.length === 0) {
    // Reserve the parent lane plus the loading lane while children fetch.
    const lanes = item.isLoadingChildren ? 1 + LOADING_CHILD_LANES : 1;
    cache?.set(item.id, lanes);
    return lanes;
  }
  const { highestOrder: childStackOrder } = stackTimelineItems(item.children);
  const result = 1 + childStackOrder + 1;
  cache?.set(item.id, result);
  return result;
};

// Lanes the child cluster spans below the parent's lane (parent lane excluded).
export const calculateClusterDepth = (
  item: TimelineItemType,
  cache?: Map<number, number>,
): number => Math.max(calculateChildLanes(item, cache) - 1, 0);

// When expanded parents share a lane and their clusters overlap horizontally, the
// later parent's cluster drops below the tallest collider; the parents stay put.
export const calculateClusterLaneOffsets = (
  itemsAtOrder: TimelineItemType[],
  expandedChildren: { [key: number]: boolean },
  itemRanks?: Map<number, string>,
  childLanesCache?: Map<number, number>,
): Map<number, number> => {
  const offsets = new Map<number, number>();

  const expandedParents = itemsAtOrder.filter(
    (item) =>
      expandedChildren[item.id] &&
      ((item.children && item.children.length > 0) || item.isLoadingChildren),
  );
  if (expandedParents.length < 2) return offsets;

  const sorted = [...expandedParents].sort((a, b) => {
    if (itemRanks && itemRanks.size > 0) {
      const aRank = itemRanks.get(a.id);
      const bRank = itemRanks.get(b.id);
      if (aRank !== undefined && bRank !== undefined) return aRank.localeCompare(bRank);
      if (aRank !== undefined) return -1;
      if (bRank !== undefined) return 1;
    }
    return sortByDateThenId(a, b);
  });

  // Placed clusters as vertical bands; a later one drops below the deepest it overlaps.
  const placed: { startMs: number; endMs: number; bandBottom: number }[] = [];

  for (const parent of sorted) {
    const footprint = getExpandedFootprintInterval(parent);
    const depth = calculateClusterDepth(parent, childLanesCache);

    let bandTop = 0;
    for (const p of placed) {
      const overlaps = footprint.startMs <= p.endMs && footprint.endMs >= p.startMs;
      if (overlaps) bandTop = Math.max(bandTop, p.bandBottom);
    }

    offsets.set(parent.id, bandTop);
    placed.push({
      startMs: footprint.startMs,
      endMs: footprint.endMs,
      bandBottom: bandTop + depth,
    });
  }

  return offsets;
};

export const sortByDateThenId = (a: TimelineItemType, b: TimelineItemType): number => {
  const aDates = getNormalizePlannedDates(a.plannedStartDateTime, a.plannedFinishDateTime);
  const bDates = getNormalizePlannedDates(b.plannedStartDateTime, b.plannedFinishDateTime);
  const aStart = aDates.start ? new Date(aDates.start).getTime() : Infinity;
  const bStart = bDates.start ? new Date(bDates.start).getTime() : Infinity;

  if (aStart !== bStart) {
    return aStart - bStart;
  }

  const aEnd = aDates.finish ? new Date(aDates.finish).getTime() : Infinity;
  const bEnd = bDates.finish ? new Date(bDates.finish).getTime() : Infinity;

  if (aEnd !== bEnd) return bEnd - aEnd;
  return a.id - b.id;
};

export const stackTimelineItems = (
  items: TimelineItemType[],
  itemRanks?: Map<number, string>,
): { stackedItems: ItemWithStackingOrder[]; highestOrder: number } => {
  const sortedItems = [...items].sort((a, b) => {
    if (itemRanks && itemRanks.size > 0) {
      const aRank = itemRanks.get(a.id);
      const bRank = itemRanks.get(b.id);
      if (aRank !== undefined && bRank !== undefined) {
        return aRank.localeCompare(bRank);
      }
      if (aRank !== undefined) return -1;
      if (bRank !== undefined) return 1;
    }
    return sortByDateThenId(a, b);
  });

  const stackedItems: ItemWithStackingOrder[] = [];
  let highestOrder = 0;

  // Track every occupied interval per order slot. Rank-based sort means items
  // don't arrive in start-date order, so a single running end time can't tell
  // whether a slot is free during an item's actual window.
  const intervalsByOrder: { start: number; end: number }[][] = [];

  for (const item of sortedItems) {
    const dates = getNormalizePlannedDates(item.plannedStartDateTime, item.plannedFinishDateTime);
    const itemStart = dates.start ? new Date(dates.start).getTime() : -Infinity;
    const itemEnd = dates.finish ? new Date(dates.finish).getTime() : Infinity;

    let order = 0;
    while (order < intervalsByOrder.length) {
      const slot = intervalsByOrder[order];
      const overlaps = slot?.some((iv) => itemStart <= iv.end && itemEnd >= iv.start);
      if (!overlaps) {
        break;
      }
      order++;
    }

    if (order >= intervalsByOrder.length) {
      intervalsByOrder.push([{ start: itemStart, end: itemEnd }]);
    } else {
      intervalsByOrder[order]?.push({ start: itemStart, end: itemEnd });
    }

    if (order > highestOrder) {
      highestOrder = order;
    }
    stackedItems.push({
      ...item,
      order,
    });
  }

  return { stackedItems, highestOrder };
};
export const calculateMaxLanesForOrder = (
  itemsAtOrder: TimelineItemType[],
  expandedChildren: { [key: number]: boolean },
  cache?: Map<number, number>,
  itemRanks?: Map<number, string>,
): number => {
  const clusterOffsets = calculateClusterLaneOffsets(
    itemsAtOrder,
    expandedChildren,
    itemRanks,
    cache,
  );
  let maxLanesForThisOrder = 1;

  for (const item of itemsAtOrder) {
    const hasLoadedChildren = !!item.children && item.children.length > 0;

    if (expandedChildren[item.id] && (hasLoadedChildren || item.isLoadingChildren)) {
      // Height must cover the dropped cluster's offset plus its own lanes.
      const lanes = (clusterOffsets.get(item.id) ?? 0) + calculateChildLanes(item, cache);
      maxLanesForThisOrder = Math.max(maxLanesForThisOrder, lanes);
    }
  }

  return maxLanesForThisOrder;
};

export const calculateLanesByOrder = (
  allItems: TimelineItemType[],
  expandedChildren: { [key: number]: boolean },
  cache?: Map<number, number>,
  itemRanks?: Map<number, string>,
): Map<number, number> => {
  const itemsByOrder = new Map<number, TimelineItemType[]>();

  for (const item of allItems) {
    const order = item.order || 0;
    if (!itemsByOrder.has(order)) {
      itemsByOrder.set(order, []);
    }
    itemsByOrder.get(order)?.push(item);
  }

  const sortedOrders = Array.from(itemsByOrder.keys()).sort((a, b) => a - b);
  const lanesByOrder = new Map<number, number>();

  for (const order of sortedOrders) {
    const itemsAtOrder = itemsByOrder.get(order) || [];
    const maxLanesForThisOrder = calculateMaxLanesForOrder(
      itemsAtOrder,
      expandedChildren,
      cache,
      itemRanks,
    );
    lanesByOrder.set(order, maxLanesForThisOrder);
  }

  return lanesByOrder;
};

// Vertical pixel offset of an item's lane within its row (sum of all lanes for
// lower orders). Mirrors the per-cell offset so a row-level overlay aligns to a card.
export const calculateItemOffset = (
  item: TimelineItemType,
  allItemsInRow: TimelineItemType[],
  expandedChildren: { [key: number]: boolean },
  laneHeight: number,
  cache?: Map<number, number>,
): number => {
  const currentOrder = item.order || 0;
  const lanesByOrder = calculateLanesByOrder(allItemsInRow, expandedChildren, cache);
  let offset = 0;
  for (const [order, lanes] of lanesByOrder.entries()) {
    if (order >= currentOrder) break;
    offset += lanes * laneHeight;
  }
  return offset;
};

// Cluster lane offset per item id across a whole row; absent means no drop.
export const calculateClusterLaneOffsetsByItem = (
  allItems: TimelineItemType[],
  expandedChildren: { [key: number]: boolean },
  itemRanks?: Map<number, string>,
  cache?: Map<number, number>,
): Map<number, number> => {
  const itemsByOrder = new Map<number, TimelineItemType[]>();
  for (const item of allItems) {
    const order = item.order || 0;
    if (!itemsByOrder.has(order)) itemsByOrder.set(order, []);
    itemsByOrder.get(order)?.push(item);
  }

  const result = new Map<number, number>();
  for (const itemsAtOrder of itemsByOrder.values()) {
    const offsets = calculateClusterLaneOffsets(itemsAtOrder, expandedChildren, itemRanks, cache);
    for (const [id, offset] of offsets) result.set(id, offset);
  }
  return result;
};
